import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { EmailAnalysisParticipantSide, EmailAnalysisTabType } from "@types";
import { env } from "@config";
import { getCurrentContextEmailProps, isDraftHtmlEmpty } from "@utils";
import { useAppDispatch, useAppSelector } from "@store";
import {
  clearSimulationResult,
  selectEmail,
  simulateDraft,
  switchSimulatorTab,
  updateThreadId,
} from "@store/simulator";
import {
  clearRephraseResult,
  currentQuestionSelector,
  getQuestionnaire,
  MessageWizardTargetType,
  rephrase,
  saveMessageWizardAnswer,
  saveMessageWizardResults,
} from "@store/messageWizard";
import { createPixel, getThreadTrackStatus, updateThreadTrackStatus } from "@store/signals";
import { useMixpanel } from "@taskpane/context";
import { withTransition } from "@taskpane/hoc";
import { useContentHeight } from "@taskpane/hooks";
import {
  CheckboxCheckedGreenIcon,
  CheckboxIcon,
  LinkBlueIcon,
  LinkIcon,
  RefreshIcon,
  RegenerateIcon,
} from "@taskpane/icons";
import {
  AnalysisSummary,
  AuthorityZone,
  AvatarVariant,
  Button,
  ButtonSize,
  ButtonVariant,
  ErrorOverlay,
  Header,
  InsightsAnalysis,
  MessageAnalysis,
  Scrollbar,
  SentimentAnalysis,
  Tabs,
  UpperHandAnalysis,
  XRayAnalysis,
} from "@taskpane/components";
import { Wizard, WizardFooter } from "./components";
import { EmptyScreen } from "../EmptyScreen";
import css from "./SimulatorScreen.module.scss";

const SimulatorScreen: React.FC = () => {
  const dispatch = useAppDispatch();

  const isComposeMode = useAppSelector((state) => state.global.isComposeMode);
  const user = useAppSelector((state) => state.auth.user);
  const result = useAppSelector((state) => state.simulator.result);
  const activeTabId = useAppSelector((state) => state.simulator.activeTabId);
  const selectedBranch = useAppSelector((state) => state.simulator.selectedBranch);
  const selectedEmail = useAppSelector((state) => state.simulator.selectedEmail);
  const currentDraftState = useAppSelector((state) => state.simulator.currentDraftState);
  const savedResults = useAppSelector((state) => state.messageWizard.savedResults);
  const isLoadingValue = useAppSelector((state) => state.simulator.isLoading.simulateDraft);
  const isError = useAppSelector((state) => state.simulator.isError.simulateDraft);
  const isPermissionError = useAppSelector((state) => state.simulator.isError.permissionError);
  const rephraseResult = useAppSelector((state) => state.messageWizard.rephraseResult);
  const isRephraseError = useAppSelector((state) => state.messageWizard.isError.rephrase);
  const isQuestionnaireError = useAppSelector((state) => state.messageWizard.isError.questionnaire);
  const currentQuestion = useAppSelector(currentQuestionSelector);
  const isTrackStatusLoading = useAppSelector((state) => state.signals.isLoading.threadTrackStatus);
  const isPixelLoading = useAppSelector((state) => state.signals.isLoading.pixel);
  const pixel = useAppSelector((state) => state.signals.pixel);
  const threadId = useAppSelector((state) => state.simulator.threadId);
  const threadTrackStatus = useAppSelector((state) => state.simulator.threadTrackStatus);

  const { trackEvent } = useMixpanel();

  const { branchId, draft } = selectedBranch || {};
  const isLoading = !result || isLoadingValue;
  const isWizardError = isRephraseError || isQuestionnaireError;

  const [isNewDraft, setIsNewDraft] = useState(false);
  const [isRecipientsEmpty, setIsRecipientsEmpty] = useState(false);
  const [isCopyEmpty, setIsCopyEmpty] = useState(false);

  const currentSimulateRequestRef = useRef<
    | (Promise<unknown> & {
        abort: () => void;
      })
    | null
  >(null);

  const headerWrapperRef = useRef<HTMLDivElement>(null);
  const contentWrapperRef = useRef<HTMLDivElement>(null);
  const footerWrapperRef = useRef<HTMLDivElement>(null);

  const tabs = useMemo(
    () => [
      {
        id: EmailAnalysisTabType.Message,
        label: "Message",
        tooltip: "Review related emails in the conversation for quick insights and easy access",
      },
      {
        id: EmailAnalysisTabType.Analysis,
        label: "Simulate",
        tooltip:
          "The simulator uses deep learning to assess how your message will be perceived by the other side.",
      },
      {
        id: EmailAnalysisTabType.XRay,
        label: "X-Ray",
        showIndicator: !!selectedEmail?.xRayAnalytics?.length,
        tooltip: "Uncover non verbal signals in the analyzed message",
      },
      {
        id: EmailAnalysisTabType.Wizard,
        label: "Wizard",
        tooltip: "Get step-by-step guidance to simulate and enhance your message replies",
      },
    ],
    [selectedEmail]
  );

  const xRayAnalysisItems = useMemo(
    () =>
      selectedEmail?.xRayAnalytics?.map(
        ({ positiveUhDirection, token, textBefore, textAfter }) => ({
          textBefore,
          token,
          textAfter,
          isPositive: positiveUhDirection,
        })
      ) || [],
    [selectedEmail]
  );

  const wizardFooterOptions = useMemo(() => {
    if (!currentQuestion && rephraseResult) {
      return [
        {
          label: "Regenerate",
          icon: RegenerateIcon,
          onClick: () => {
            trackEvent("simulatorScreen.wizardRegenerateClick");
            dispatch(clearRephraseResult());
          },
        },
        {
          label: "Reset",
          icon: RefreshIcon,
          onClick: () => {
            if (draft && branchId) {
              trackEvent("simulatorScreen.wizardResetClick");
              dispatch(
                saveMessageWizardResults({
                  branchId,
                  platformDraftId: draft.platformDraftId,
                  platformThreadId: draft.platformThreadId,
                  results: savedResults,
                  isCompleted: true,
                })
              );
            }
          },
        },
      ];
    }

    if (!currentQuestion) {
      return [];
    }

    return currentQuestion.options.map((option) => ({
      label: option.text,
      onClick: () => {
        dispatch(
          saveMessageWizardAnswer({
            answer: {
              questionId: currentQuestion.wqId,
              answerId: option.optionId,
            },
            isCompleted: option.target.type === MessageWizardTargetType.FinishAction,
          })
        );
      },
    }));
  }, [currentQuestion, rephraseResult, trackEvent]);

  const headerOptions = useMemo(
    () =>
      isNewDraft
        ? []
        : [
            {
              id: "trackStatus",
              label: "Track",
              icon: threadTrackStatus ? CheckboxCheckedGreenIcon : CheckboxIcon,
              disabled: threadTrackStatus === null || isTrackStatusLoading,
              tooltip: threadTrackStatus
                ? "Disable tracking of email opens and participant changes."
                : "Enable tracking of email opens and participant changes.",
              onClick: () => {
                dispatch(updateThreadTrackStatus({ threadId, onTrack: !threadTrackStatus }));
              },
            },
          ],
    [dispatch, isNewDraft, threadTrackStatus, isTrackStatusLoading]
  );

  const handleSimulateDraft = useCallback(async () => {
    if (!isComposeMode || result || !user) {
      return;
    }

    const {
      conversationId,
      draftHtml,
      draftText,
      messageId,
      outlookProfileEmail,
      participants,
      recipients,
      subject,
    } = await getCurrentContextEmailProps();

    if (messageId) {
      currentSimulateRequestRef.current?.abort();
      currentSimulateRequestRef.current = dispatch(
        simulateDraft({
          conversationId,
          draftHtml,
          draftText,
          messageId,
          outlookProfileEmail,
          participants,
          recipients,
          subject,
        })
      );
    }
  }, [isComposeMode, result, user, dispatch]);

  const checkDraftChanges = useCallback(async () => {
    if (!isComposeMode || !user) {
      return;
    }

    const { draftHtml, draftText, subject, recipients, conversationId } =
      await getCurrentContextEmailProps();
    const isRecipientsEmpty = recipients.length === 0;
    const isCopyEmpty = isDraftHtmlEmpty(draftHtml);

    setIsNewDraft(!conversationId);
    setIsRecipientsEmpty(isRecipientsEmpty);
    setIsCopyEmpty(isCopyEmpty);

    const newDraftState = JSON.stringify({
      draftText,
      subject,
      recipients,
    });

    if (
      newDraftState !== currentDraftState &&
      !isRecipientsEmpty &&
      !isCopyEmpty &&
      conversationId
    ) {
      dispatch(clearSimulationResult());
      dispatch(updateThreadId(conversationId));
      handleSimulateDraft();
    }
  }, [dispatch, handleSimulateDraft, currentDraftState, isComposeMode, user]);

  const emptyScreenContent = useMemo(() => {
    const attrs = {
      emptyScreenContent: {
        className: css.emptyScreenContent,
      },
      emptyScreenContentTitle: {
        className: css.emptyScreenContentTitle,
      },
      emptyScreenContentText: {
        className: css.emptyScreenContentText,
      },
    };

    if (!isComposeMode) {
      return (
        <div {...attrs.emptyScreenContent}>
          <div {...attrs.emptyScreenContentTitle}>Simulator</div>
          <div {...attrs.emptyScreenContentText}>
            <span>Simulation is only available for reply messages. To use the Simulator:</span>
          </div>
          <div {...attrs.emptyScreenContentText}>
            <ol>
              <li>
                <span>Close the add-in.</span>
              </li>
              <li>
                <span>Click &quot;Reply&quot; to start drafting your response.</span>
              </li>
              <li>
                <span>Reopen Substrata for Outlook from the ribbon.</span>
              </li>
            </ol>
          </div>
        </div>
      );
    }

    if (isNewDraft) {
      return (
        <div {...attrs.emptyScreenContent}>
          <div {...attrs.emptyScreenContentTitle}>Simulator</div>
          <span>Simulation is only available for reply messages.</span>
        </div>
      );
    }

    return (
      <div {...attrs.emptyScreenContent}>
        <div {...attrs.emptyScreenContentTitle}>Simulator</div>
        <span>
          Type your reply to simulate how it will be received and get actionable insights.
        </span>
      </div>
    );
  }, [isComposeMode, isCopyEmpty, isRecipientsEmpty, isNewDraft]);

  const attrs = {
    container: {
      className: css.container,
    },
    headerWrapper: {
      ref: headerWrapperRef,
      className: css.headerWrapper,
    },
    contentWrapper: {
      ref: contentWrapperRef,
      className: css.contentWrapper,
    },
    emptyScreen: {
      text: emptyScreenContent,
    },
    header: {
      isLoading,
      title: "Simulator",
      options: headerOptions,
    },
    authorityZone: {
      isLoading,
      selectedBranch,
      selectedEmail,
      isDraft: true,
      branches: result?.branchResults || [],
      onSelectEmail: (messageId: string) => {
        dispatch(selectEmail(messageId));
        dispatch(switchSimulatorTab(EmailAnalysisTabType.Message));
      },
    },
    tabsWrapper: {
      className: css.tabsWrapper,
    },
    tabs: {
      tabs,
      activeTabId,
      disabled: isLoading,
      onTabClick: (tabId: EmailAnalysisTabType) => {
        trackEvent(`simulatorScreen.${tabId}TabClick`);
        dispatch(switchSimulatorTab(tabId));
      },
    },
    scrollbar: {
      className: css.scrollbar,
    },
    content: {
      className: css.content,
    },
    messageAnalysis: {
      isLoading,
      currentIndex: selectedEmail?.index,
      sender:
        selectedEmail?.sender === EmailAnalysisParticipantSide.Left
          ? selectedEmail?.participants?.leftParticipant
          : selectedEmail?.participants?.rightParticipant,
      text: selectedEmail?.html || null,
      totalCount: selectedBranch?.emails?.length + 1 || 0,
    },
    upperHandAnalysis: {
      isLoading,
      dominantSide: selectedEmail?.dominantParticipant || "unresolved",
      leftParticipant: {
        email: selectedEmail?.participants?.leftParticipant?.email || "",
        name:
          selectedEmail?.participants?.leftParticipant?.metadata?.name ||
          selectedEmail?.participants?.leftParticipant?.email ||
          "",
        avatarProps: {
          src: selectedEmail?.participants?.leftParticipant?.metadata?.avatarSrc || "",
          name:
            selectedEmail?.participants?.leftParticipant?.metadata?.name ||
            selectedEmail?.participants?.leftParticipant?.email ||
            "",
          variant: selectedEmail?.participants?.leftParticipant?.isUser
            ? AvatarVariant.User
            : AvatarVariant.Prospect,
          ...(isLoading && {
            variant: AvatarVariant.Default,
          }),
        },
      },
      rightParticipant: {
        email: selectedEmail?.participants?.rightParticipant?.email || "",
        name:
          selectedEmail?.participants?.rightParticipant?.metadata?.name ||
          selectedEmail?.participants?.rightParticipant?.email ||
          "",
        avatarProps: {
          src: selectedEmail?.participants?.rightParticipant?.metadata?.avatarSrc || "",
          name:
            selectedEmail?.participants?.rightParticipant?.metadata?.name ||
            selectedEmail?.participants?.rightParticipant?.email ||
            "",
          variant: selectedEmail?.participants?.rightParticipant?.isUser
            ? AvatarVariant.User
            : AvatarVariant.Prospect,
          ...(isLoading && {
            variant: AvatarVariant.Default,
          }),
        },
      },
    },
    sentimentAnalysis: {
      isLoading,
      value: selectedEmail?.sentiment?.mag || null,
      name:
        selectedEmail?.sender === EmailAnalysisParticipantSide.Left
          ? selectedEmail?.participants?.leftParticipant?.metadata?.name || ""
          : selectedEmail?.participants?.rightParticipant?.metadata?.name || "",
    },
    analysisSummary: {
      isLoading,
      items: selectedEmail?.summary || [],
    },
    insightsAnalysis: {
      isLoading,
      value: selectedBranch?.simulationFeedback?.draftFeedback || null,
    },
    openAnalysisButton: {
      disabled: isLoading || !selectedBranch?.emails?.length,
      variant: ButtonVariant.Secondary,
      size: ButtonSize.S,
      onClick: () => {
        if (user && selectedBranch) {
          trackEvent("simulatorScreen.openAnalysisClick");
          window.open(
            `${env.accountOrigin}/orgs/${user.orgId}/users/${user.userId}/touch-points/${(selectedBranch.branchId || "").replace(/-draft.*/, "")}`,
            "_blank",
            "noopener,noreferrer"
          );
        }
      },
    },
    xRayAnalysis: {
      isLoading,
      items: xRayAnalysisItems,
    },
    wizard: {
      isNewDraft,
    },
    footerWrapper: {
      ref: footerWrapperRef,
      className: css.footerWrapper,
    },
    wizardFooter: {
      options: wizardFooterOptions,
    },
    errorOverlay: {
      isOpened: isError,
      buttonIcon: isPermissionError ? LinkIcon : RefreshIcon,
      description: isPermissionError
        ? "Connect your Microsoft account to continue viewing your account data"
        : "Please refresh to try again",
      title: "Simulation error",
      buttonText: isPermissionError ? "Continue" : "Refresh",
      onClick: () => {
        if (isPermissionError) {
          window.open(
            `${env.accountOrigin}/orgs/${user?.orgId}/users/${user?.userId}`,
            "_blank",
            "noopener,noreferrer"
          );
        } else {
          dispatch(clearSimulationResult());
          handleSimulateDraft();
        }
      },
    },
    wizardErrorOverlay: {
      isOpened: isWizardError,
      buttonIcon: RefreshIcon,
      description: "Please refresh to try again",
      title: "Wizard error",
      onClick: async () => {
        if (isQuestionnaireError && branchId && draft) {
          dispatch(
            getQuestionnaire({
              branchId,
              platformDraftId: draft.platformDraftId,
              platformThreadId: draft.platformThreadId,
            })
          );
        } else if (isRephraseError && draft) {
          const { draftText } = await getCurrentContextEmailProps();

          if (draftText) {
            dispatch(rephrase({ currentText: draftText, draft, savedResults }));
          }
        }
      },
    },
  };

  const renderActiveTabContent = () => {
    if (activeTabId === EmailAnalysisTabType.Message) {
      return <MessageAnalysis {...attrs.messageAnalysis} />;
    }

    if (activeTabId === EmailAnalysisTabType.Analysis) {
      return (
        <>
          <UpperHandAnalysis {...attrs.upperHandAnalysis} />
          <SentimentAnalysis {...attrs.sentimentAnalysis} />
          <AnalysisSummary {...attrs.analysisSummary} />
          <InsightsAnalysis {...attrs.insightsAnalysis} />
          <Button {...attrs.openAnalysisButton}>
            <span>Open full analysis</span>
            <LinkBlueIcon />
          </Button>
        </>
      );
    }

    if (activeTabId === EmailAnalysisTabType.XRay) {
      return <XRayAnalysis {...attrs.xRayAnalysis} />;
    }

    if (activeTabId === EmailAnalysisTabType.Wizard) {
      return <Wizard {...attrs.wizard} />;
    }

    return null;
  };

  useEffect(() => {
    trackEvent("simulatorScreen.view");
  }, [trackEvent]);

  useContentHeight(contentWrapperRef, [headerWrapperRef, footerWrapperRef], {
    offset: 73,
    useWindowHeight: true,
    deps: [isLoading],
  });

  useEffect(() => {
    const interval = window.setInterval(checkDraftChanges, 500);

    return () => {
      window.clearInterval(interval);
    };
  }, [checkDraftChanges]);

  useEffect(() => {
    if (isComposeMode && threadId && threadTrackStatus === null) {
      dispatch(getThreadTrackStatus({ threadId }));
    }
  }, [isComposeMode, threadId, threadTrackStatus, dispatch]);

  useEffect(() => {
    (async () => {
      if (!threadId || pixel || isPixelLoading) {
        return;
      }

      const { messageId } = await getCurrentContextEmailProps();

      if (messageId) {
        dispatch(
          createPixel({
            dataMessageId: messageId,
            platformThreadId: threadId,
          })
        );
      }
    })();
  }, [pixel, isPixelLoading, threadId, dispatch]);

  useEffect(() => {
    if (!pixel || !threadTrackStatus) {
      Office.context.mailbox.item?.body?.appendOnSendAsync?.(null);
      return;
    }

    const { url } = pixel;

    Office.context.mailbox.item?.body?.appendOnSendAsync?.(
      `<img src="${url}" width="1" height="1" style="display:none;" />`,
      { coercionType: Office.CoercionType.Html },
      (result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          console.log("Body appended successfully");
        } else {
          console.error("Error appending body: ", result.error);
        }
      }
    );
  }, [pixel, threadTrackStatus]);

  if (!isComposeMode || isRecipientsEmpty || isCopyEmpty || isNewDraft) {
    return <EmptyScreen {...attrs.emptyScreen} />;
  }

  return (
    <div {...attrs.container}>
      <div {...attrs.headerWrapper}>
        <Header {...attrs.header} />
        <AuthorityZone {...attrs.authorityZone} />
        <div {...attrs.tabsWrapper}>
          <Tabs {...attrs.tabs} />
        </div>
      </div>
      <div {...attrs.contentWrapper}>
        <Scrollbar {...attrs.scrollbar}>
          <div {...attrs.content}>{renderActiveTabContent()}</div>
        </Scrollbar>
        {activeTabId === EmailAnalysisTabType.Wizard ? (
          <ErrorOverlay {...attrs.wizardErrorOverlay} />
        ) : null}
      </div>
      <div {...attrs.footerWrapper}>
        {activeTabId === EmailAnalysisTabType.Wizard ? (
          <WizardFooter {...attrs.wizardFooter} />
        ) : null}
      </div>
      <ErrorOverlay {...attrs.errorOverlay} />
    </div>
  );
};

export default withTransition(SimulatorScreen);
